import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, HeartHandshake, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import DisclosureBanner from "../components/DisclosureBanner";
import { AVP_SHIFTS } from "../lib/avpShifts";

export default function AvpInterestResponse() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [shift, setShift] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/avp-interest/${token}`).then(({ data }) => { setInfo(data); setShift(data.shift || ""); setState(data.response ? "already" : "choose"); })
      .catch((e) => { setError(formatApiError(e)); setState("invalid"); });
  }, [token]);

  const respond = async (choice) => {
    setBusy(true);
    try { const { data } = await api.post(`/avp-interest/${token}/respond`, { choice }); setInfo((i) => ({ ...i, ...data })); setState("done"); }
    catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  };
  const final = info?.response;
  const interested = final?.startsWith("interested");

  return (
    <div>
      <DisclosureBanner />
      <section className="py-16 min-h-[60vh] flex items-center px-4">
        <div className="max-w-xl mx-auto w-full rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8 text-center">
          <img src="/logo.png" alt="Integrity Delivered" className="h-16 w-auto mx-auto mb-6" />
          {state === "loading" && <div data-testid="avp-interest-loading" className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>}
          {state === "invalid" && (
            <div data-testid="avp-interest-invalid">
              <h1 className="text-2xl font-extrabold uppercase display text-white">Link not valid</h1>
              <p className="mt-3 text-sm text-slate-400">{error || "Please use the link in your AVP e-mail or call 667-434-4392."}</p>
            </div>
          )}
          {state === "choose" && (
            <div data-testid="avp-interest-choose">
              <h1 className="text-2xl sm:text-3xl font-extrabold uppercase display text-white">Are you still interested{info?.first_name ? `, ${info.first_name}` : ""}?</h1>
              <p className="mt-3 text-sm text-slate-300">Alternative Vehicle Program · Part-Time Priority Delivery. Please choose your preferred shift and confirm below.</p>
              <div className="mt-6 grid gap-2 text-left">
                {Object.entries(AVP_SHIFTS).map(([k, s]) => (
                  <button key={k} type="button" data-testid={`avp-interest-shift-${k}`} onClick={() => setShift(k)} className={`rounded-xl border p-3 text-sm transition-colors ${shift === k ? "border-amber-500 bg-amber-500/10 text-white" : "border-slate-700 text-slate-200 hover:border-slate-500"}`}>
                    <span className="font-bold">{s.time}</span> <span className="text-slate-400">· {s.label}</span>
                  </button>
                ))}
              </div>
              {error && <p data-testid="avp-interest-error" className="mt-3 text-sm text-rose-300">{error}</p>}
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Button data-testid="avp-interest-yes" size="lg" disabled={!shift || busy} onClick={() => respond(`interested_${shift}`)} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider">I'm Still Interested</Button>
                <Button data-testid="avp-interest-no" size="lg" variant="outline" disabled={busy} onClick={() => window.confirm("Withdraw your AVP application?") && respond("withdraw")} className="border-slate-600 text-slate-200 uppercase tracking-wider">No Longer Interested</Button>
              </div>
              {!shift && <p className="mt-2 text-xs text-slate-500">Select a shift to confirm your interest.</p>}
            </div>
          )}
          {(state === "done" || state === "already") && (
            <div data-testid={`avp-interest-${interested ? "interested" : "withdraw"}`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${interested ? "bg-emerald-500/15 border border-emerald-500/40" : "bg-slate-800 border border-slate-700"}`}>
                {interested ? <CheckCircle2 className="w-8 h-8 text-emerald-400" /> : <HeartHandshake className="w-8 h-8 text-slate-300" />}
              </div>
              <h1 className="mt-6 text-2xl sm:text-3xl font-extrabold uppercase display text-white">{interested ? "Thank you — you're in the review queue" : "Your AVP application has been withdrawn"}</h1>
              <p data-testid="avp-interest-message" className="mt-4 text-slate-300 leading-relaxed">
                {interested
                  ? <>We've recorded your interest in <strong className="text-white">{info?.shift_label}</strong>. Our management team will review your application. If we move forward, you'll receive an e-mail asking for your vehicle details — no documents are needed until then.</>
                  : "Thank you for letting us know. Your record stays on file, and you're welcome to apply again if your circumstances change."}
              </p>
              {state === "already" && <p data-testid="avp-interest-already" className="mt-3 text-xs text-slate-500">We already have your response on file from {new Date(info.response_at).toLocaleString()}.</p>}
              <div className="mt-6 text-xs text-slate-500 flex items-center justify-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Integrity Delivered, Inc. · Recruiting</div>
              <div className="mt-6 flex gap-3 justify-center flex-wrap">
                <Link to={`/avp?t=${token}`}><Button variant="outline" className="border-slate-700 text-slate-200">AVP program details</Button></Link>
                <Link to="/"><Button variant="outline" className="border-slate-700 text-slate-200">Back to home</Button></Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
